<template>
  <div class="px-4 md:px-8 py-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-[22px] md:text-[24px] font-semibold text-bodyText">Create Event</h1>
        <p class="text-sm text-grayDark">Publish an event for workspace or project.</p>
      </div>
    </div>

    <div class="bg-white border border-gray-100 rounded-[12px] shadow-sm max-w-4xl mx-auto">
      <form @submit.prevent="submit">
        <div v-if="message" :class="['px-6 py-3 rounded-t-[12px]', isSuccess ? 'bg-green-50' : 'bg-red-50']">
          <p :class="isSuccess ? 'text-green-600' : 'text-red-600'">{{ message }}</p>
        </div>

        <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div class="md:col-span-2">
            <label class="flex items-center gap-1 text-sm text-gray-600 mb-1">Name <span class="text-red-500">*</span></label>
            <input v-model.trim="form.name" :class="['w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30', errors.name ? 'border-red-300' : 'border-gray-200']" />
            <p v-if="errors.name" class="text-xs text-red-500 mt-1">{{ errors.name }}</p>
          </div>

          <div class="md:col-span-2">
            <label class="flex items-center gap-1 text-sm text-gray-600 mb-1">Description <span class="text-red-500">*</span></label>
            <textarea v-model.trim="form.description" rows="6" :class="['w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30', errors.description ? 'border-red-300' : 'border-gray-200']"></textarea>
            <p v-if="errors.description" class="text-xs text-red-500 mt-1">{{ errors.description }}</p>
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">Start date/time <span class="text-red-500">*</span></label>
            <input type="datetime-local" v-model="form.start_date" class="w-full border border-gray-200 rounded px-3 py-2" />
            <p v-if="errors.start_date" class="text-xs text-red-500 mt-1">{{ errors.start_date }}</p>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">End date/time</label>
            <input type="datetime-local" v-model="form.end_date" class="w-full border border-gray-200 rounded px-3 py-2" />
            <p v-if="errors.end_date" class="text-xs text-red-500 mt-1">{{ errors.end_date }}</p>
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">Location</label>
            <input v-model.trim="form.location" class="w-full border border-gray-200 rounded px-3 py-2" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-gray-600 mb-1">Latitude</label>
              <input v-model.number="form.latitude" type="number" step="any" class="w-full border border-gray-200 rounded px-3 py-2" />
            </div>
            <div>
              <label class="block text-sm text-gray-600 mb-1">Longitude</label>
              <input v-model.number="form.longitude" type="number" step="any" class="w-full border border-gray-200 rounded px-3 py-2" />
            </div>
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">Workspace</label>
            <select v-model.number="form.workspace_id" class="w-full border border-gray-200 rounded px-3 py-2">
              <option :value="null">None</option>
              <option v-for="w in workspaces" :key="w.id" :value="w.id">{{ w.name }}</option>
            </select>
          </div>
          <div class="relative">
            <label class="block text-sm text-gray-600 mb-1">Project</label>
            <div class="flex items-center gap-2">
              <div class="flex-1">
                <div @click="toggleProjectDropdown" class="w-full border border-gray-200 rounded px-3 py-2 cursor-pointer bg-white flex items-center justify-between">
                  <span class="text-sm text-gray-700 truncate" :title="selectedProjectName || 'Select a project'">
                    {{ selectedProjectName || 'Select a project' }}
                  </span>
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                </div>
              </div>
              <button v-if="form.project_id" type="button" @click="clearSelectedProject" class="text-xs text-gray-600 underline">Clear</button>
            </div>
            <div v-if="showProjectDropdown" class="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
              <div class="p-2 border-b border-gray-100">
                <input v-model.trim="projectSearch" @input="debouncedFetchProjects" class="w-full border border-gray-200 rounded px-2 py-1 text-sm" placeholder="Search projects..." />
              </div>
              <div class="max-h-56 overflow-auto">
                <div v-if="projectLoading" class="p-3 text-xs text-gray-400">Loading...</div>
                <template v-else>
                  <button
                    v-for="p in projects"
                    :key="p.project_id || p.id"
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    @click="selectProject(p)"
                  >
                    <div class="truncate" :title="p.project_name">{{ p.project_name }}</div>
                    <div class="text-[11px] text-gray-400">{{ p.description }}</div>
                  </button>
                  <div v-if="!projects.length" class="p-3 text-xs text-gray-400">No projects found</div>
                </template>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <input id="is_public" type="checkbox" v-model="form.is_public" class="h-4 w-4" />
            <label for="is_public" class="text-sm text-gray-600">Public event</label>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Max attendees</label>
            <input v-model.number="form.max_attendees" type="number" min="1" class="w-full border border-gray-200 rounded px-3 py-2" />
          </div>

          <div class="flex items-center gap-3">
            <input id="registration_required" type="checkbox" v-model="form.registration_required" class="h-4 w-4" />
            <label for="registration_required" class="text-sm text-gray-600">Registration required</label>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Registration deadline</label>
            <input type="datetime-local" v-model="form.registration_deadline" class="w-full border border-gray-200 rounded px-3 py-2" />
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">Event type</label>
            <select v-model="form.event_type" class="w-full border border-gray-200 rounded px-3 py-2">
              <option :value="null">Select type</option>
              <option value="workshop">Workshop</option>
              <option value="conference">Conference</option>
              <option value="meetup">Meetup</option>
              <option value="training">Training</option>
              <option value="bootcamp">Bootcamp</option>
              <option value="webinar">Webinar</option>
              <option value="launch">Launch</option>
              <option value="networking">Networking</option>
              <option value="design">Design</option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Tags (comma separated)</label>
            <input v-model="tagsInput" class="w-full border border-gray-200 rounded px-3 py-2" placeholder="e.g. product, design" />
            <div v-if="parsedTags.length" class="mt-2 flex flex-wrap gap-2">
              <span v-for="t in parsedTags" :key="t" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primaryDarkBg/10 text-primaryDarkBg">#{{ t }}</span>
            </div>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 mb-1">Image URL</label>
            <input v-model.trim="form.image_url" class="w-full border border-gray-200 rounded px-3 py-2" />
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">External URL</label>
            <input v-model.trim="form.external_url" class="w-full border border-gray-200 rounded px-3 py-2" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-gray-600 mb-1">Cost</label>
              <input v-model.number="form.cost" type="number" min="0" step="0.01" class="w-full border border-gray-200 rounded px-3 py-2" />
            </div>
            <div>
              <label class="block text-sm text-gray-600 mb-1">Currency</label>
              <input v-model.trim="form.currency" class="w-full border border-gray-200 rounded px-3 py-2" placeholder="USD" />
            </div>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 mb-1">Requirements</label>
            <textarea v-model.trim="form.requirements" rows="3" class="w-full border border-gray-200 rounded px-3 py-2"></textarea>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 mb-1">Agenda</label>
            <textarea v-model.trim="form.agenda" rows="3" class="w-full border border-gray-200 rounded px-3 py-2"></textarea>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 mb-1">Contact info</label>
            <textarea v-model.trim="form.contact_info" rows="2" class="w-full border border-gray-200 rounded px-3 py-2"></textarea>
          </div>
        </div>

        <div class="px-6 pb-6 flex items-center gap-3">
          <button :disabled="submitting" type="submit" class="px-4 py-2 bg-primaryDarkBg text-primaryLight rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed">{{ submitting ? 'Saving...' : 'Publish' }}</button>
          <button type="button" @click="saveDraft" :disabled="submitting" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed">Save Draft</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios';

export default {
  name: 'CreateEventComponent',
  data() {
    return {
      submitting: false,
      message: '',
      isSuccess: false,
      workspaces: [],
      projects: [],
      projectSearch: '',
      projectLoading: false,
      showProjectDropdown: false,
      tagsInput: '',
      form: {
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        location: '',
        latitude: null,
        longitude: null,
        workspace_id: null,
        project_id: null,
        is_public: true,
        max_attendees: null,
        registration_required: false,
        registration_deadline: '',
        event_type: null,
        tags: [],
        image_url: '',
        external_url: '',
        cost: null,
        currency: '',
        requirements: '',
        agenda: '',
        contact_info: ''
      },
      errors: {},
      projectSearchTimeout: null
    }
  },
  computed: {
    parsedTags() {
      if (!this.tagsInput) return [];
      return this.tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    },
    selectedProjectName() {
      const p = this.projects.find(pr => pr.project_id === this.form.project_id);
      return p ? p.project_name : '';
    }
  },
  methods: {
    validate() {
      const e = {};
      if (!this.form.name?.trim()) e.name = 'Name is required';
      if (!this.form.description?.trim()) e.description = 'Description is required';
      if (!this.form.start_date) e.start_date = 'Start date is required';
      if (this.form.end_date && this.form.start_date && new Date(this.form.end_date) <= new Date(this.form.start_date)) {
        e.end_date = 'End date must be after start date';
      }
      if (this.form.registration_deadline && this.form.start_date && new Date(this.form.registration_deadline) >= new Date(this.form.start_date)) {
        e.registration_deadline = 'Registration deadline must be before start date';
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    async fetchWorkspaces() {
      try {
        const res = await apiClient.get('/general/workspaces');
        this.workspaces = (res.data && res.data.data) || [];
      } catch (e) {
        console.warn('Failed to fetch workspaces', e);
      }
    },
    async fetchProjects(search = '') {
      try {
        this.projectLoading = true;
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        const url = params.toString() ? `/web/projects_all?${params.toString()}` : '/web/projects_all';
        const res = await apiClient.get(url);
        const list = (res.data && (res.data.data || res.data)) || [];
        this.projects = Array.isArray(list) ? list : [];
      } catch (e) {
        console.warn('Failed to fetch projects', e);
        this.projects = [];
      } finally {
        this.projectLoading = false;
      }
    },
    debouncedFetchProjects() {
      if (this.projectSearchTimeout) clearTimeout(this.projectSearchTimeout);
      this.projectSearchTimeout = setTimeout(() => this.fetchProjects(this.projectSearch), 300);
    },
    toggleProjectDropdown() {
      this.showProjectDropdown = !this.showProjectDropdown;
      if (this.showProjectDropdown && !this.projects.length) this.fetchProjects();
    },
    selectProject(p) {
      this.form.project_id = p.project_id;
      this.showProjectDropdown = false;
    },
    clearSelectedProject() {
      this.form.project_id = null;
    },
    async submit() {
      if (!this.validate()) return;
      this.submitting = true;
      this.message = '';
      this.isSuccess = false;
      try {
        const payload = { ...this.form };
        payload.tags = this.parsedTags;
        if (!payload.end_date) payload.end_date = null;
        if (!payload.registration_deadline) payload.registration_deadline = null;
        if (!payload.workspace_id) payload.workspace_id = null;
        if (!payload.project_id) payload.project_id = null;
        if (!payload.event_type) payload.event_type = null;

        const res = await apiClient.post('/general/events', payload);
        if (res && res.data && (res.data.success || res.data.status === 'success')) {
          this.isSuccess = true;
          this.message = 'Event created successfully';
          this.$router.push({ name: 'ExploreEvents' });
        } else {
          this.message = (res && res.data && res.data.message) || 'Failed to create event';
        }
      } catch (e) {
        console.error('Create event error', e);
        this.message = (e.response && e.response.data && (e.response.data.detail || e.response.data.message)) || 'An error occurred';
      } finally {
        this.submitting = false;
      }
    },
    async saveDraft() {
      // No explicit draft status in contract; using same endpoint with minimal required fields
      await this.submit();
    }
  },
  created() {
    this.fetchWorkspaces();
    // this.fetchProjects();
  }
}
</script>

<style scoped>
.text-green-600 { color: #16a34a }
.text-red-600 { color: #dc2626 }
</style>